import React, { useState, useEffect } from 'react';
import { UserConfigService, UserConfigurationRequest, UserConfigurationResponse, FolderInfo, DatabaseFolderValidationResult } from '../utils/apiService';
import { useConfig } from '../contexts/ConfigContext';
import { useAuth, AuthState } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface UserConfigManagerProps {
  // Removed onClose as it's not being used
}

const UserConfigManager: React.FC<UserConfigManagerProps> = () => {
  const { 
    configurations,
    loadConfigurations, 
    markFirstTimeSetup
  } = useConfig();
  const { authState } = useAuth();
  const [folders, setFolders] = useState<FolderInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState<UserConfigurationResponse | null>(null);
  const [foldersLoaded, setFoldersLoaded] = useState(false);

  // Simplified form state - now includes organization_name
  const [formData, setFormData] = useState<UserConfigurationRequest>({
    folder_id: '',
    display_name: '',
    organization_name: ''
  });
  
  const [validationResult, setValidationResult] = useState<DatabaseFolderValidationResult | null>(null);
  const [validating, setValidating] = useState(false);

  // Load folders only after authentication is confirmed
  useEffect(() => {
    const loadFoldersIfAuthenticated = async () => {
      if (authState === AuthState.AUTHENTICATED && !foldersLoaded) {
        await loadFolders();
        setFoldersLoaded(true);
        setLoading(false);
      }
    };
    
    loadFoldersIfAuthenticated();
  }, [authState, foldersLoaded]);

  // If user has no configurations, show the create form immediately
  useEffect(() => {
    if (configurations.length === 0 && !loading && authState === AuthState.AUTHENTICATED) {
      setShowCreateForm(true);
    }
  }, [configurations, loading, authState]);

  const loadFolders = async () => {
    try {
      setLoading(true);
      const response = await UserConfigService.getAccessibleFolders();
      setFolders(response);
    } catch (err) {
      console.error('Error loading folders:', err);
      setError('Failed to load accessible folders. You may need to manually enter a folder ID.');
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  const validateFolder = async (folderId: string) => {
    if (!folderId) {
      setValidationResult(null);
      return;
    }
    
    try {
      setValidating(true);
      const result = await UserConfigService.validateFolder({ folder_id: folderId });
      setValidationResult(result);
    } catch (err) {
      console.error('Error validating folder:', err);
      setValidationResult({
        folder_accessible: false,
        error_message: 'Failed to validate folder access'
      });
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validationResult?.folder_accessible) {
      toast.error('Please select a valid folder that you have access to');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editingConfig) {
        await UserConfigService.updateConfiguration(editingConfig.config_id, formData);
        toast.success(`Configuration "${formData.display_name}" updated successfully!`);
      } else {
        const newConfig = await UserConfigService.createConfiguration(formData);
        toast.success(`Configuration "${newConfig.display_name}" created successfully!`);
        markFirstTimeSetup(false);
      }
      
      // Force a reload of configurations from the server
      await loadConfigurations(true);
      
      // The App.tsx navigation logic will handle the redirect automatically
      // after the configuration state is updated.
      resetForm();

    } catch (err: any) {
      console.error('Error saving configuration:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to save configuration';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (configId: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa configurazione?')) return;
    
    try {
      setLoading(true);
      await UserConfigService.deleteConfiguration(configId);
      await loadConfigurations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossibile eliminare la configurazione');
      console.error('Error deleting configuration:', err);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (config: UserConfigurationResponse) => {
    setEditingConfig(config);
    setFormData({
      folder_id: config.folder_id,
      display_name: config.display_name,
      organization_name: config.organization_name
    });
    setShowCreateForm(true);
    validateFolder(config.folder_id);
  };

  const resetForm = () => {
    setShowCreateForm(false);
    setEditingConfig(null);
    setFormData({
      folder_id: '',
      display_name: '',
      organization_name: ''
    });
    setValidationResult(null);
    setError(null);
  };

  const handleFolderChange = (folderId: string) => {
    try {
      setFormData(prev => ({ ...prev, folder_id: folderId }));
      
      // Auto-suggest display name based on folder name
      if (folderId && !editingConfig && Array.isArray(folders) && folders.length > 0) {
        try {
          const selectedFolder = folders.find(f => f && f.folder_id === folderId);
          if (selectedFolder && selectedFolder.folder_name && !formData.display_name) {
            setFormData(prev => ({ ...prev, display_name: selectedFolder.folder_name }));
          }
        } catch (suggestionError) {
          console.warn('Error in auto-suggestion:', suggestionError);
        }
      }
      
      if (folderId) {
        validateFolder(folderId);
      } else {
        setValidationResult(null);
      }
    } catch (error) {
      console.error('Error in handleFolderChange:', error);
      // Still update the folder_id even if other operations fail
      setFormData(prev => ({ ...prev, folder_id: folderId }));
    }
  };

  if (loading && !foldersLoaded) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Caricamento...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Configurazione Google Drive
        </h2>
        
        {error && (
          <div className="mb-4 p-4 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20">
            <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        {configurations.length > 0 && !showCreateForm && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Le Tue Configurazioni ({configurations.length})
              </h3>
              <button
                onClick={() => setShowCreateForm(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                disabled={loading}
              >
                + Aggiungi Cartella
              </button>
            </div>
            
            <div className="space-y-4">
              {configurations.map(config => (
                <div key={config.config_id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                          {config.display_name}
                        </h4>

                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                        <p><span className="font-medium">Organizzazione:</span> {config.organization_name}</p>
                        <p><span className="font-medium">Collezione:</span> {config.collection_name}</p>
                        <p><span className="font-medium">ID Cartella:</span> {config.folder_id}</p>
                        <p><span className="font-medium">Creata:</span> {new Date(config.created_at).toLocaleDateString('it-IT')}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => startEdit(config)}
                        className="px-3 py-1 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md"
                        disabled={loading}
                      >
                        Modifica
                      </button>
                      {configurations.length > 1 && (
                        <button
                          onClick={() => handleDelete(config.config_id)}
                          className="px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                          disabled={loading}
                        >
                          Elimina
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(showCreateForm || configurations.length === 0) && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingConfig ? 'Modifica Configurazione' : 'Aggiungi Nuova Cartella Google Drive'}
              </h3>
              {configurations.length > 0 && (
                <button
                  onClick={resetForm}
                  className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                  disabled={loading}
                >
                  Annulla
                </button>
              )}
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cartella Google Drive
                </label>
                {Array.isArray(folders) && folders.length > 0 ? (
                  <select
                    value={formData.folder_id}
                    onChange={(e) => handleFolderChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={loading}
                  >
                    <option value="">Seleziona una cartella</option>
                    {folders.map(folder => (
                      <option key={folder.folder_id} value={folder.folder_id}>
                        {folder.folder_name} ({folder.folder_path})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.folder_id}
                    onChange={(e) => handleFolderChange(e.target.value)}
                    placeholder="Inserisci l'ID della cartella Google Drive"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={loading}
                  />
                )}
                
                {validating && (
                  <p className="mt-1 text-sm text-blue-600">Verifica accesso alla cartella...</p>
                )}
                
                {validationResult && (
                  <div className={`mt-2 p-3 rounded ${
                    validationResult.folder_accessible 
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                  }`}>
                    {validationResult.folder_accessible ? (
                      <div>
                        <p className="font-medium">✓ Cartella accessibile</p>
                        {validationResult.folder_name && (
                          <p className="text-sm">Nome: {validationResult.folder_name}</p>
                        )}
                        {validationResult.folder_path && (
                          <p className="text-sm">Percorso: {validationResult.folder_path}</p>
                        )}
                      </div>
                    ) : (
                      <p>✗ {validationResult.error_message}</p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome Collezione
                </label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                  placeholder="es. 'Documenti di Ricerca', 'File Aziendali'"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={loading}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Un nome descrittivo per identificare questa collezione
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome Organizzazione
                </label>
                <input
                  type="text"
                  value={formData.organization_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, organization_name: e.target.value }))}
                  placeholder="es. 'Azienda A', 'QuTec srl'"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={loading}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Il nome della tua organizzazione (questo separa i tuoi documenti da altri utenti)
                </p>
              </div>



              <div className="flex justify-end space-x-4">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !validationResult?.folder_accessible}
                >
                  {loading ? 'Salvataggio...' : (editingConfig ? 'Aggiorna Configurazione' : 'Crea Configurazione')}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserConfigManager;